<template>
  <div id="project-tracker-table">
    <k-table v-if="projectsReady && projects.length > 0"
      :headers="kableHeaders"
      :rows="kableRows"
      :hideable="false"
      :panel="false"
      :max="10"
      @clicked="handleTableAction"
    ></k-table>
  </div>
</template>

<style scoped>
:deep(.status-badge.completed) {
  background-color: var(--kate-success);
  color: var(--kate-type-dark);
}

:deep(.status-badge.scoping_proposed),
:deep(.status-badge.in_progress) {
  background-color: var(--kate-type-primary);
  color: var(--kate-type-dark);
}

:deep(.status-badge.review) {
  background-color: var(--kate-primary);
  color: var(--kate-type-dark);
}

:deep(.status-badge.draft) {
  background-color: var(--kate-type-dark);
  color: var(--kate-type-light);
}

</style>

<script>
import KTable from '@base-components/tables/display/k-table.vue';

export default {
  components: {
    KTable,
  },

  props: {
    projects: {
      type: Array,
    },
    projectsReady: {
      type: Boolean,
      default: false,
    },
    openFeedbackModal: {
      type: Function,
    },
    openEvidencePage: {
      type: Function,
    },
  },

  computed: {
    programmeId() {
      return Number(this.$route.params.programmeId);
    },

    kableHeaders() {
      return {
        phase: {
          name: 'Phase',
          type: 'function',
          filterable: true,
          sortable: true,
          options: {
            callback: this.phaseDisplay,
          },
        },
        name: {
          name: 'Project Name',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        description: {
          name: 'Project Description',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        competencies: {
          name: 'Competencies',
          type: 'tags',
          filterable: true,
          sortable: true,
        },
        status: {
          name: 'Status',
          type: 'function',
          filterable: true,
          sortable: true,
          options: {
            callback: this.statusDisplay,
          },
        },
        all_feedback: {
          name: 'View All Feedback',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
        scope: {
          name: 'Project Scope',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
        evidence: {
          name: 'Project Evidence',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
      };
    },

    kableRows() {
      return this.projects.map(project => ({
        id: project.id,
        phase: project.status,
        status: project.status,
        name: project.name,
        description: project.description,
        scoping_completed: project.scoping_completed,
        project_completed: project.project_completed,
        competencies: {
          tags: project.competencies ? project.competencies.map(c => c.label) : [],
          sortBy: project.competencies ? project.competencies.map(c => c.label).join(',') : '',
        },
        scoping_document_link: project.scoping_document_link,
        all_feedback: {
          text: this.tableActionButtonDisplay(project, 'feedback'),
        },
        scope: {
          text: this.tableActionButtonDisplay(project, 'scope'),
        },
        evidence: {
          text: this.tableActionButtonDisplay(project, 'evidence'),
        },
      }));
    },
  },

  methods: {

    phaseDisplay(row) {
      if (row.phase === 'in_progress' && row.scoping_completed) {
        return 'Creation';
      }
      if (row.phase === 'scoping_proposed' && !row.scoping_completed) {
        return 'Scoping';
      }
      if (row.phase === 'completed') {
        return 'Completed';
      }
      return 'Draft';
    },

    statusDisplay(row) {
      const statusTextMap = {
        draft: 'Draft',
        scoping_proposed: 'Scope proposed',
        in_progress: 'In progress',
        completed: 'Complete',
      };

      const statusText = statusTextMap[row.status] || 'Draft';
      const statusClass = String(row.status).toLowerCase();

      return `<div class="status-cell"><span class="status-badge ${statusClass}">${statusText}</span></div>`;
    },

    handleTableAction(key, index, row, column) {
      if (column === 'all_feedback') {
        this.$emit('view-all-feedback', row.id);
      } else if (column === 'scope') {
        this.$emit('edit-project', row.id);
      } else if (column === 'evidence') {
        this.openEvidencePage(row.id);
      }
    },

    tableActionButtonDisplay(row, action) {
      switch (action) {
        case 'feedback':
          return this.getFeedbackButton(row);
        case 'scope':
          return this.getScopeButton(row);
        case 'evidence':
          return this.getEvidenceButton(row);
        default:
          return this.getDefaultButton();
      }
    },

    getFeedbackButton(row) {
      const disabledAttr = row.status !== 'draft' ? '' : ' disabled';
      return `<button class="btn btn-primary" aria-label="Open" title="Open"${disabledAttr}><i class="fas fa-external-link-alt"></i></button>`;
    },

    getScopeButton(row) {
      const isViewMode = row.scoping_completed || row.project_completed;
      const icon = isViewMode ? 'fas fa-external-link-alt' : 'fas fa-pencil-alt';
      const label = isViewMode ? 'Open' : 'Edit';

      return `<button class="btn btn-primary" aria-label="${label}" title="${label}"><i class="${icon}"></i></button>`;
    },

    getEvidenceButton(row) {
      const isEnabled = row.status !== 'draft' && row.scoping_completed;
      const isViewMode = row.status === 'completed';
      const icon = isViewMode ? 'fas fa-external-link-alt' : 'fas fa-pencil-alt';
      const label = isViewMode ? 'Open' : 'Edit';
      const disabledAttr = isEnabled ? '' : ' disabled';

      return `<button class="btn btn-primary" aria-label="${label}" title="${label}"${disabledAttr}><i class="${icon}"></i></button>`;
    },

    getDefaultButton() {
      return '<button class="btn btn-primary" aria-label="Open" title="Open" disabled><i class="fas fa-external-link-alt"></i></button>';
    },
  },
};
</script>
